import * as React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useParams, useLocation, useSearch } from "wouter";
import {
  Loader2,
  Trash2,
  ArrowUpDown,
  Rows3,
  LayoutList,
  Pin,
  PinOff,
  Plus,
  ChevronDown,
  MoreHorizontal,
  FolderInput,
  Folder as FolderIcon,
  X,
  Pencil,
  Lock,
  Merge,
  Download,
  Info,
  Eraser,
  Archive,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { NoteOverlay } from "@/components/note-overlay";
import { NoteActionsMenu } from "@/components/note-actions-menu";
import {
  useListNotes,
  useDeleteNote,
  useTogglePinNote,
  useListFolders,
  useCreateFolder,
  useAssignNoteFolder,
  useRenameFolder,
  useDeleteFolder,
  useArchiveNote,
  useArchiveFolder,
  useDuplicateNote,
  useMergeNotes,
  getListNotesQueryKey,
  getListFoldersQueryKey,
  type Note,
} from "@workspace/api-client-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { stripHtml } from "@/lib/strip-html";
import type { Folder } from "@workspace/api-client-react";
export function BrowserPage() {
  const [folderSearchQuery, setFolderSearchQuery] = React.useState("");
  const queryClient = useQueryClient();
  const [, setLocation] = useLocation();
  const { id } = useParams<{ id?: string }>();
  const [sortOrder, setSortOrder] = React.useState<
    "newest" | "oldest" | "title-asc" | "title-desc"
  >("newest");
  const [viewMode, setViewMode] = React.useState<"list" | "compact">("list");
  const search = useSearch();
  const searchParams = React.useMemo(
    () => new URLSearchParams(search),
    [search],
  );
  const folderIdParam = searchParams.get("folderId");
  const activeFolderId = folderIdParam != null ? Number(folderIdParam) : null;
  // Drives folder selection through the URL (instead of local state) so
  // that navigating away — e.g. into the quick-create fullscreen editor —
  // and back via the browser's real back button/swipe-back restores the
  // same folder. The URL itself is the source of truth.
  const setActiveFolder = (folderId: number | null) => {
    const basePath = id ? `/browse/${id}` : "/browse";
    const target =
      folderId != null ? `${basePath}?folderId=${folderId}` : basePath;
    // Only push a fresh history entry the first time we leave "All Notes"
    // for a folder, so there's an "All Notes" entry to swipe back to.
    // Switching between folders after that (or back to "All") replaces the
    // current entry instead of stacking a new one, so a single swipe back
    // from any folder always lands on "All Notes" — never a previously
    // visited folder.
    setLocation(target, { replace: activeFolderId != null });
  };
  const [isCreatingFolder, setIsCreatingFolder] = React.useState(false);
  const [newFolderName, setNewFolderName] = React.useState("");
  const [openMoveSheet, setOpenMoveSheet] = React.useState(false);
  const [openMergeSheet, setOpenMergeSheet] = React.useState(false);
  const [mergeSourceId, setMergeSourceId] = React.useState<number | null>(
    null,
  );
  const [openAddNotesSheet, setOpenAddNotesSheet] = React.useState(false);
  const [selectedNoteIdsToAdd, setSelectedNoteIdsToAdd] = React.useState(
    new Set<number>(),
  );
  const [openFolderPickerSheet, setOpenFolderPickerSheet] =
    React.useState(false);

  // If arriving from a link that wants the folder picker opened
  // immediately (e.g. the "Folders" quick-link on the Capture page),
  // open it once on mount and clean the one-shot flag out of the URL.
  // folderId is NOT touched here — it's the persistent source of truth for
  // activeFolderId now (see setActiveFolder above), so it stays in the URL.
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("openFolders") === "1") {
      setOpenFolderPickerSheet(true);
      params.delete("openFolders");
      const newSearch = params.toString();
      const newUrl =
        window.location.pathname + (newSearch ? `?${newSearch}` : "");
      window.history.replaceState(null, "", newUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [selectedNoteId, setSelectedNoteId] = React.useState<number | null>(
    null,
  );
  const [confirmDeleteId, setConfirmDeleteId] = React.useState<number | null>(
    null,
  );
  const [folderToDelete, setFolderToDelete] = React.useState<Folder | null>(
    null,
  );
  const [deleteNotesInside, setDeleteNotesInside] = React.useState(false);

  // Unfiltered notes list, needed to know which notes belong to any given
  // folder when the user chooses to delete them along with the folder.
  const { data: allNotesUnfiltered = [] } = useListNotes();

  const { data: folders = [] } = useListFolders();
  const createFolder = useCreateFolder();
  const assignFolder = useAssignNoteFolder();
  const renameFolder = useRenameFolder();
  const deleteFolder = useDeleteFolder();

  // ADD THIS HERE (after folders is declared)
  const filteredFolders = folders.filter((folder) =>
    folder.name.toLowerCase().includes(folderSearchQuery.toLowerCase()),
  );

  const folderColorPalette = [
    {
      bg: "bg-blue-100 dark:bg-blue-950",
      text: "text-blue-600 dark:text-blue-400",
    },
    {
      bg: "bg-green-100 dark:bg-green-950",
      text: "text-green-600 dark:text-green-400",
    },
    {
      bg: "bg-orange-100 dark:bg-orange-950",
      text: "text-orange-600 dark:text-orange-400",
    },
    {
      bg: "bg-purple-100 dark:bg-purple-950",
      text: "text-purple-600 dark:text-purple-400",
    },
    {
      bg: "bg-pink-100 dark:bg-pink-950",
      text: "text-pink-600 dark:text-pink-400",
    },
    {
      bg: "bg-teal-100 dark:bg-teal-950",
      text: "text-teal-600 dark:text-teal-400",
    },
  ];

  const getFolderColor = (folderId: number) =>
    folderColorPalette[folderId % folderColorPalette.length];
  const [renamingFolder, setRenamingFolder] = React.useState<Folder | null>(
    null,
  );
  const [renameValue, setRenameValue] = React.useState("");

  const { data: allNotes = [], isLoading: isLoadingNotes } = useListNotes(
    activeFolderId != null ? { folderId: activeFolderId } : undefined,
  );

  const notes = React.useMemo(() => {
    const sorted = [...allNotes];
    switch (sortOrder) {
      case "oldest":
        return sorted.sort(
          (a, b) =>
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
        );
      case "title-asc":
        return sorted.sort((a, b) =>
          (a.title || "").localeCompare(b.title || ""),
        );
      case "title-desc":
        return sorted.sort((a, b) =>
          (b.title || "").localeCompare(a.title || ""),
        );
      default:
        return sorted.sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        );
    }
  }, [allNotes, sortOrder]);

  const deleteNote = useDeleteNote();
  const togglePin = useTogglePinNote();
  const archiveNote = useArchiveNote();
  const duplicateNote = useDuplicateNote();
  const mergeNotes = useMergeNotes();
  const archiveFolder = useArchiveFolder();

  const handleTogglePin = (id: number) => {
    togglePin.mutate(
      { id },
      {
        onSuccess: (updated) => {
          const updateList = (list: Note[] | undefined) =>
            list?.map((n) =>
              n.id === id ? { ...n, pinned: updated.pinned } : n,
            );
          queryClient.setQueryData<Note[]>(
            getListNotesQueryKey(
              activeFolderId != null ? { folderId: activeFolderId } : undefined,
            ),
            updateList,
          );
        },
      },
    );
  };

  const openNoteId = id ? Number(id) : null;
  const openNote =
    openNoteId != null ? allNotes.find((n) => n.id === openNoteId) : undefined;

  const handleOpen = (note: Note) =>
    setLocation(
      `/browse/${note.id}${activeFolderId != null ? `?folderId=${activeFolderId}` : ""}`,
    );
  const handleCloseOverlay = () => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      setLocation(
        activeFolderId != null
          ? `/browse?folderId=${activeFolderId}`
          : "/browse",
      );
    }
  };

  const notesNotInFolder = React.useMemo(
    () => allNotesUnfiltered.filter((n) => n.folderId !== activeFolderId),
    [allNotesUnfiltered, activeFolderId],
  );
  const toggleSelectedNoteToAdd = (noteId: number) => {
    setSelectedNoteIdsToAdd((prev) => {
      const next = new Set(prev);
      if (next.has(noteId)) {
        next.delete(noteId);
      } else {
        next.add(noteId);
      }
      return next;
    });
  };
  const handleAddSelectedNotes = () => {
    if (activeFolderId == null) return;
    selectedNoteIdsToAdd.forEach((noteId) => {
      handleMoveToFolder(noteId, activeFolderId);
    });
    setSelectedNoteIdsToAdd(new Set());
    setOpenAddNotesSheet(false);
  };
  const handleMoveToFolder = (noteId: number, folderId: number | null) => {
    assignFolder.mutate(
      { id: noteId, data: { folderId: folderId ?? undefined } },
      {
        onSuccess: (updated) => {
          const patch = (list: Note[] | undefined) =>
            list?.map((n) => (n.id === updated.id ? updated : n));
          const remove = (list: Note[] | undefined) =>
            list?.filter((n) => n.id !== updated.id);
          // If we're currently viewing a specific folder and the note moved
          // to a different one, drop it from this list instead of patching it.
          const listKey = getListNotesQueryKey(
            activeFolderId != null ? { folderId: activeFolderId } : undefined,
          );
          if (activeFolderId != null && activeFolderId !== folderId) {
            queryClient.setQueryData<Note[]>(listKey, remove);
          } else {
            queryClient.setQueryData<Note[]>(listKey, patch);
          }
        },
      },
    );
  };

  const handleDelete = (id: number) => {
    deleteNote.mutate(
      { id },
      {
        onSuccess: () => {
          const remove = (list: Note[] | undefined) =>
            list?.filter((n) => n.id !== id);
          queryClient.setQueryData<Note[]>(
            getListNotesQueryKey(
              activeFolderId != null ? { folderId: activeFolderId } : undefined,
            ),
            remove,
          );
        },
      },
    );
  };

  const handleArchive = (id: number) => {
    archiveNote.mutate(
      { id },
      {
        onSuccess: () => {
          const remove = (list: Note[] | undefined) =>
            list?.filter((n) => n.id !== id);
          queryClient.setQueryData<Note[]>(
            getListNotesQueryKey(
              activeFolderId != null ? { folderId: activeFolderId } : undefined,
            ),
            remove,
          );
        },
      },
    );
  };

  const handleDuplicate = (id: number) => {
    duplicateNote.mutate(
      { id },
      {
        onSuccess: (created) => {
          queryClient.setQueryData<Note[]>(
            getListNotesQueryKey(
              activeFolderId != null ? { folderId: activeFolderId } : undefined,
            ),
            (list) => (list ? [created, ...list] : [created]),
          );
        },
      },
    );
  };

  const handleMergeNotes = (id: number) => {
    setMergeSourceId(id);
    setOpenMergeSheet(true);
  };

  const handleSelectMergeTarget = (secondNoteId: number) => {
    if (mergeSourceId === null) return;
    mergeNotes.mutate(
      { id: mergeSourceId, data: { secondNoteId } },
      {
        onSuccess: (created) => {
          const listKey = getListNotesQueryKey(
            activeFolderId != null ? { folderId: activeFolderId } : undefined,
          );
          queryClient.setQueryData<Note[]>(listKey, (list) => {
            const filtered = list?.filter(
              (n) => n.id !== mergeSourceId && n.id !== secondNoteId,
            );
            return filtered ? [created, ...filtered] : [created];
          });
          setOpenMergeSheet(false);
          setMergeSourceId(null);
          handleOpen(created);
        },
      },
    );
  };

  const handleArchiveFolder = (folder: Folder) => {
    archiveFolder.mutate(
      { id: folder.id },
      {
        onSuccess: () => {
          queryClient.setQueryData<typeof folders>(
            getListFoldersQueryKey(),
            (old) => old?.filter((f) => f.id !== folder.id),
          );
          if (activeFolderId === folder.id) setActiveFolder(null);
        },
      },
    );
  };

  const handleCreateFolder = () => {
    const name = newFolderName.trim();
    if (!name) {
      setIsCreatingFolder(false);
      return;
    }
    createFolder.mutate(
      { data: { name } },
      {
        onSuccess: (folder) => {
          queryClient.setQueryData(
            getListFoldersQueryKey(),
            (old: typeof folders | undefined) => [...(old ?? []), folder],
          );
          setNewFolderName("");
          setIsCreatingFolder(false);
          setActiveFolder(folder.id);
        },
      },
    );
  };
  const handleRenameFolder = (folder: Folder) => {
    setRenamingFolder(folder);
    setRenameValue(folder.name);
  };

  const submitRenameFolder = () => {
    const name = renameValue.trim();
    if (!renamingFolder || !name || name === renamingFolder.name) {
      setRenamingFolder(null);
      return;
    }
    renameFolder.mutate(
      { id: renamingFolder.id, data: { name } },
      {
        onSuccess: (updated) => {
          queryClient.setQueryData<typeof folders>(
            getListFoldersQueryKey(),
            (old) => old?.map((f) => (f.id === updated.id ? updated : f)),
          );
          setRenamingFolder(null);
        },
      },
    );
  };

  const handleDeleteFolder = (folder: Folder) => {
    setFolderToDelete(folder);
    setDeleteNotesInside(false);
  };

  const submitDeleteFolder = () => {
    if (!folderToDelete) return;
    const folderId = folderToDelete.id;

    const finalizeFolderDelete = () => {
      deleteFolder.mutate(
        { id: folderId },
        {
          onSuccess: () => {
            queryClient.setQueryData<typeof folders>(
              getListFoldersQueryKey(),
              (old) => old?.filter((f) => f.id !== folderId),
            );
            if (activeFolderId === folderId) setActiveFolder(null);
            queryClient.invalidateQueries({ queryKey: getListNotesQueryKey() });
          },
        },
      );
    };

    if (deleteNotesInside) {
      const notesInFolder = allNotesUnfiltered.filter(
        (n) => n.folderId === folderId,
      );
      if (notesInFolder.length === 0) {
        finalizeFolderDelete();
      } else {
        let remaining = notesInFolder.length;
        notesInFolder.forEach((n) => {
          deleteNote.mutate(
            { id: n.id },
            {
              onSuccess: () => {
                remaining -= 1;
                queryClient.setQueryData<Note[]>(
                  getListNotesQueryKey(),
                  (list) => list?.filter((x) => x.id !== n.id),
                );
                if (remaining === 0) finalizeFolderDelete();
              },
            },
          );
        });
      }
    } else {
      finalizeFolderDelete();
    }

    setFolderToDelete(null);
  };

  return (
    <div className="max-w-4xl mx-auto h-[calc(100vh-4rem-3.25rem)] flex flex-col px-4 relative">
      <div className="flex items-center gap-2 pt-3 pb-3 shrink-0">
        {/* Folder filter row */}
        <div className="flex items-center gap-2 overflow-x-auto flex-1 min-w-0">
          <button
            type="button"
            onClick={() => setActiveFolder(null)}
            className={cn(
              "shrink-0 px-4 py-1.5 rounded-full text-sm font-medium border transition-all",
              activeFolderId === null
                ? "gradient-primary text-white border-transparent"
                : "bg-card/50 border-border/50 text-muted-foreground",
            )}
          >
            All
          </button>

          <button
            type="button"
            onClick={() => setOpenFolderPickerSheet(true)}
            className={cn(
              "shrink-0 flex items-center gap-1 px-4 py-1.5 rounded-full text-sm font-medium border transition-all",
              activeFolderId !== null
                ? "gradient-primary text-white border-transparent"
                : "bg-card/50 border-border/50 text-muted-foreground",
            )}
          >
            {activeFolderId !== null
              ? (folders.find((f) => f.id === activeFolderId)?.name ?? "Folder")
              : "Folders"}
            <ChevronDown className="h-3.5 w-3.5" />
          </button>

          <button
            type="button"
            onClick={() => {
              const params = new URLSearchParams();
              params.set("expanded", "1");
              if (activeFolderId != null) {
                params.set("folderId", String(activeFolderId));
              }
              setLocation(`/?${params.toString()}`);
            }}
            className="shrink-0 h-8 w-8 rounded-full border border-border/50 bg-card/50 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Create note"
          >
            <Plus className="h-4 w-4" />
          </button>
        </div>

        <div className="flex items-center gap-1.5 shrink-0">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8 rounded-lg"
              >
                <ArrowUpDown className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setSortOrder("newest")}>
                Newest First
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setSortOrder("oldest")}>
                Oldest First
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setSortOrder("title-asc")}>
                Title (A → Z)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setSortOrder("title-desc")}>
                Title (Z → A)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8 rounded-lg"
            onClick={() =>
              setViewMode(viewMode === "list" ? "compact" : "list")
            }
          >
            {viewMode === "list" ? (
              <Rows3 className="h-4 w-4" />
            ) : (
              <LayoutList className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>

      <ScrollArea className="flex-1 -mx-4 px-4 pb-6">
        <div className="flex flex-col gap-3 py-2">
          {isLoadingNotes ? (
            <div className="flex justify-center text-muted-foreground items-center gap-2 py-10">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span>Searching what you've saved...</span>
            </div>
          ) : notes.length === 0 ? (
            <div className="text-center text-muted-foreground py-20">
              Nothing here yet. Give this space something to remember.
            </div>
          ) : (
            notes.map((note) =>
              viewMode === "compact" ? (
                <div
                  key={note.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleOpen(note)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleOpen(note);
                  }}
                  className="group grid grid-cols-[1fr_auto] items-center gap-2 px-3 py-2.5 border-b last:border-b-0 cursor-pointer hover:bg-muted/40 transition-colors"
                >
                  <span className="text-[15px] font-medium truncate min-w-0">
                    {note.title || stripHtml(note.content)}
                  </span>
                  <div className="flex items-center gap-1.5">
                    <div>
                      <NoteActionsMenu
                        note={note}
                        onTogglePin={handleTogglePin}
                        onMoveTo={(id) => {
                          setSelectedNoteId(id);
                          setOpenMoveSheet(true);
                        }}
                        onDelete={(id) => setConfirmDeleteId(id)}
                        onArchive={handleArchive}
                      onDuplicate={handleDuplicate}
                      onMergeNotes={handleMergeNotes}
                        isPinPending={togglePin.isPending}
                        isDeletePending={deleteNote.isPending}
                        isArchivePending={archiveNote.isPending}
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  key={note.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleOpen(note)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleOpen(note);
                  }}
                  className="group relative bg-card border rounded-2xl px-5 py-4 pr-12 shadow-sm animate-in fade-in slide-in-from-bottom-1 duration-300 cursor-pointer hover:border-primary/40 transition-colors"
                >
                  {note.title && (
                    <p className="text-[15px] font-semibold leading-relaxed mb-0.5 truncate">
                      {note.title}
                    </p>
                  )}
                  {note.imageUrls && note.imageUrls.length > 0 && note.imagePosition !== "after" && (<div className="relative mb-2"><img src={note.imageUrls[0]} alt="" className="w-full h-32 object-cover rounded-xl" />{note.imageUrls.length > 1 && (<span className="absolute bottom-1 right-1 text-[10px] font-medium bg-black/60 text-white rounded-full px-1.5 py-0.5">+{note.imageUrls.length - 1}</span>)}</div>)}
                  <p className="text-[15px] leading-relaxed text-muted-foreground line-clamp-2 whitespace-pre-wrap">
                    {stripHtml(note.content)}
                  </p>
                  {note.imageUrls && note.imageUrls.length > 0 && note.imagePosition === "after" && (<div className="relative mt-2"><img src={note.imageUrls[0]} alt="" className="w-full h-32 object-cover rounded-xl" />{note.imageUrls.length > 1 && (<span className="absolute bottom-1 right-1 text-[10px] font-medium bg-black/60 text-white rounded-full px-1.5 py-0.5">+{note.imageUrls.length - 1}</span>)}</div>)}
                  <span className="block text-[11px] text-muted-foreground/60 font-medium mt-2">
                    {format(new Date(note.createdAt), "MMM d, yyyy · h:mm a")}
                  </span>

                  <div className="absolute right-3 top-3">
                    <NoteActionsMenu
                      note={note}
                      onTogglePin={handleTogglePin}
                      onMoveTo={(id) => {
                        setSelectedNoteId(id);
                        setOpenMoveSheet(true);
                      }}
                      onDelete={(id) => setConfirmDeleteId(id)}
                      onArchive={handleArchive}
                      onDuplicate={handleDuplicate}
                      onMergeNotes={handleMergeNotes}
                      isPinPending={togglePin.isPending}
                      isDeletePending={deleteNote.isPending}
                      isArchivePending={archiveNote.isPending}
                    />
                  </div>
                </div>
              ),
            )
          )}
        </div>
      </ScrollArea>
      <Sheet open={openMoveSheet} onOpenChange={setOpenMoveSheet}>
        <SheetContent
          side="bottom"
          className="rounded-t-2xl flex flex-col max-h-[80vh] overflow-hidden [&>button.absolute]:hidden"
        >
          <div className="relative flex flex-col min-h-0">
            <div className="relative flex items-center justify-center py-1">
              <h2 className="text-sm font-semibold">Move to folder</h2>
              <button
                onClick={() => setOpenMoveSheet(false)}
                className="absolute right-0 h-8 w-8 rounded-full border flex items-center justify-center text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            {/* Search bar */}
            <input
              type="text"
              placeholder="Search folders..."
              value={folderSearchQuery}
              onChange={(e) => setFolderSearchQuery(e.target.value)}
              className="w-full px-4 py-2.5 rounded-lg bg-muted border border-transparent focus:outline-none focus:border-primary text-sm mt-2"
            />

            {/* Scrollable folder list */}
            <ScrollArea className="flex-1 min-h-0 overflow-y-auto -mx-6 px-6 my-3">
              <div className="border rounded-lg overflow-hidden">
                {/* No folder */}
                <button
                  className="w-full text-left px-4 py-2.5 hover:bg-muted active:scale-[0.98] transition text-sm border-b"
                  onClick={() => {
                    if (selectedNoteId !== null) {
                      handleMoveToFolder(selectedNoteId, null);
                    }
                    setOpenMoveSheet(false);
                    setFolderSearchQuery("");
                  }}
                >
                  <div className="inline-flex items-center gap-2">
                    <div className="h-9 w-9 rounded-full bg-muted flex items-center justify-center shrink-0">
                      <FolderIcon className="h-4 w-4 text-muted-foreground" />
                    </div>
                    No folder
                  </div>
                </button>

                {/* Filtered folder list */}
                {filteredFolders.length === 0 ? (
                  <div className="px-4 py-3 text-center text-xs text-muted-foreground border-t">
                    No folders found
                  </div>
                ) : (
                  filteredFolders.map((folder) => (
                    <div
                      key={folder.id}
                      className="flex items-center justify-between px-4 py-2.5 border-b last:border-b-0 hover:bg-muted transition"
                    >
                      {/* LEFT: folder name + icon */}
                      <button
                        className="flex-1 text-left flex items-center gap-2 min-w-0"
                        onClick={() => {
                          if (selectedNoteId !== null) {
                            handleMoveToFolder(selectedNoteId, folder.id);
                          }
                          setOpenMoveSheet(false);
                          setFolderSearchQuery("");
                        }}
                      >
                        <div
                          className={cn(
                            "h-9 w-9 rounded-full flex items-center justify-center shrink-0",
                            getFolderColor(folder.id).bg,
                          )}
                        >
                          <FolderIcon
                            className={cn(
                              "h-4 w-4",
                              getFolderColor(folder.id).text,
                            )}
                          />
                        </div>
                        <span className="text-sm font-medium truncate">
                          {folder.name}
                        </span>
                      </button>

                      {/* RIGHT: ⋯ MENU */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-6 w-6 text-muted-foreground"
                            onClick={(e) => e.stopPropagation()}
                          >
                            ⋯
                          </Button>
                        </DropdownMenuTrigger>

                        <DropdownMenuContent
                          align="end"
                          collisionPadding={8}
                          className="rounded-2xl p-1.5 w-44 shadow-lg border-none space-y-1 max-h-[var(--radix-dropdown-menu-content-available-height)] overflow-y-auto"
                        >
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRenameFolder(folder);
                            }}
                            className="rounded-lg px-2.5 py-2 gap-2 text-sm font-medium bg-muted/60 focus:bg-muted"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                            Rename
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={(e) => e.stopPropagation()}
                            className="rounded-lg px-2.5 py-2 gap-2 text-sm font-medium bg-muted/60 focus:bg-muted"
                          >
                            <FolderInput className="h-3.5 w-3.5" />
                            Move to
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={(e) => e.stopPropagation()}
                            className="rounded-lg px-2.5 py-2 gap-2 text-sm font-medium bg-muted/60 focus:bg-muted"
                          >
                            <Lock className="h-3.5 w-3.5" />
                            Make Private
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={(e) => e.stopPropagation()}
                            className="rounded-lg px-2.5 py-2 gap-2 text-sm font-medium bg-muted/60 focus:bg-muted"
                          >
                            <Merge className="h-3.5 w-3.5" />
                            Merge Folders
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={(e) => e.stopPropagation()}
                            className="rounded-lg px-2.5 py-2 gap-2 text-sm font-medium bg-muted/60 focus:bg-muted"
                          >
                            <Download className="h-3.5 w-3.5" />
                            Export Folder
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={(e) => e.stopPropagation()}
                            className="rounded-lg px-2.5 py-2 gap-2 text-sm font-medium bg-muted/60 focus:bg-muted"
                          >
                            <Info className="h-3.5 w-3.5" />
                            Info
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={(e) => e.stopPropagation()}
                            className="rounded-lg px-2.5 py-2 gap-2 text-sm font-medium bg-muted/60 focus:bg-muted"
                          >
                            <Eraser className="h-3.5 w-3.5" />
                            Empty Folder
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              handleArchiveFolder(folder);
                            }}
                            className="rounded-lg px-2.5 py-2 gap-2 text-sm font-medium bg-muted/60 focus:bg-muted"
                          >
                            <Archive className="h-3.5 w-3.5" />
                            Archive
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteFolder(folder);
                            }}
                            className="rounded-lg px-2.5 py-2 gap-2 text-sm font-medium text-red-500 focus:text-red-600"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  ))
                )}
              </div>
            </ScrollArea>

            {/* Create folder button */}
            <button
              className="w-full text-left px-4 py-2.5 rounded-lg text-primary hover:bg-muted active:scale-[0.98] transition text-sm font-medium border"
              onClick={() => {
                setOpenMoveSheet(false);
                setFolderSearchQuery("");
                setIsCreatingFolder(true);
              }}
            >
              + Create new folder
            </button>
          </div>
        </SheetContent>
      </Sheet>
      <Sheet open={openMergeSheet} onOpenChange={setOpenMergeSheet}>
        <SheetContent
          side="bottom"
          className="rounded-t-2xl flex flex-col max-h-[80vh] overflow-hidden [&>button.absolute]:hidden"
        >
          <div className="relative flex flex-col min-h-0">
            <div className="relative flex items-center justify-center py-1">
              <h2 className="text-sm font-semibold">Merge with</h2>
              <button
                onClick={() => {
                  setOpenMergeSheet(false);
                  setMergeSourceId(null);
                }}
                className="absolute right-0 h-8 w-8 rounded-full border flex items-center justify-center text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <ScrollArea className="flex-1 min-h-0 overflow-y-auto -mx-6 px-6 my-3">
              <div className="border rounded-lg overflow-hidden">
                {allNotes.filter((n) => n.id !== mergeSourceId).length ===
                0 ? (
                  <div className="px-4 py-3 text-center text-xs text-muted-foreground">
                    No other notes to merge with
                  </div>
                ) : (
                  allNotes
                    .filter((n) => n.id !== mergeSourceId)
                    .map((n) => (
                      <button
                        key={n.id}
                        className="w-full text-left px-4 py-2.5 hover:bg-muted active:scale-[0.98] transition text-sm border-b last:border-b-0"
                        onClick={() => handleSelectMergeTarget(n.id)}
                      >
                        <div className="font-medium truncate">
                          {n.title || "Untitled"}
                        </div>
                      </button>
                    ))
                )}
              </div>
            </ScrollArea>
          </div>
        </SheetContent>
      </Sheet>
      <Sheet
        open={openFolderPickerSheet}
        onOpenChange={setOpenFolderPickerSheet}
      >
        <SheetContent
          side="bottom"
          className="rounded-t-2xl flex flex-col max-h-[80vh] overflow-hidden [&>button.absolute]:hidden"
        >
          <div className="relative flex flex-col min-h-0">
            <div className="relative flex items-center justify-center py-1">
              <h2 className="text-sm font-semibold">Filter by folder</h2>
              <button
                onClick={() => setOpenFolderPickerSheet(false)}
                className="absolute right-0 h-8 w-8 rounded-full border flex items-center justify-center text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Search bar */}
            <input
              type="text"
              placeholder="Search folders..."
              value={folderSearchQuery}
              onChange={(e) => setFolderSearchQuery(e.target.value)}
              className="w-full px-4 py-2.5 rounded-lg bg-muted border border-transparent focus:outline-none focus:border-primary text-sm mt-2"
            />

            {/* Scrollable folder list */}
            <ScrollArea className="flex-1 min-h-0 overflow-y-auto -mx-6 px-6 my-3">
              <div className="border rounded-lg overflow-hidden">
                {/* All notes */}
                <button
                  className="w-full text-left px-4 py-2.5 hover:bg-muted active:scale-[0.98] transition text-sm border-b"
                  onClick={() => {
                    setActiveFolder(null);
                    setOpenFolderPickerSheet(false);
                    setFolderSearchQuery("");
                  }}
                >
                  <div className="inline-flex items-center gap-2">
                    <div className="h-9 w-9 rounded-full bg-muted flex items-center justify-center shrink-0">
                      <FolderIcon className="h-4 w-4 text-muted-foreground" />
                    </div>
                    All notes
                  </div>
                </button>

                {/* Filtered folder list */}
                {filteredFolders.length === 0 ? (
                  <div className="px-4 py-3 text-center text-xs text-muted-foreground border-t">
                    No folders found
                  </div>
                ) : (
                  filteredFolders.map((folder) => (
                    <div
                      key={folder.id}
                      className={cn(
                        "flex items-center justify-between px-4 py-2.5 border-b last:border-b-0 transition",
                        activeFolderId === folder.id
                          ? "bg-primary/10"
                          : "hover:bg-muted",
                      )}
                    >
                      {/* LEFT: folder name + icon */}
                      <button
                        className="flex-1 text-left flex items-center gap-2 min-w-0"
                        onClick={() => {
                          setActiveFolder(folder.id);
                          setOpenFolderPickerSheet(false);
                          setFolderSearchQuery("");
                        }}
                      >
                        <div
                          className={cn(
                            "h-9 w-9 rounded-full flex items-center justify-center shrink-0",
                            getFolderColor(folder.id).bg,
                          )}
                        >
                          <FolderIcon
                            className={cn(
                              "h-4 w-4",
                              getFolderColor(folder.id).text,
                            )}
                          />
                        </div>
                        <span
                          className={cn(
                            "text-sm font-medium truncate",
                            activeFolderId === folder.id && "text-primary",
                          )}
                        >
                          {folder.name}
                        </span>
                      </button>

                      {/* RIGHT: ⋯ MENU */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-6 w-6 text-muted-foreground"
                            onClick={(e) => e.stopPropagation()}
                          >
                            ⋯
                          </Button>
                        </DropdownMenuTrigger>

                        <DropdownMenuContent
                          align="end"
                          collisionPadding={8}
                          className="rounded-2xl p-1.5 w-44 shadow-lg border-none space-y-1 max-h-[var(--radix-dropdown-menu-content-available-height)] overflow-y-auto"
                        >
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRenameFolder(folder);
                            }}
                            className="rounded-lg px-2.5 py-2 gap-2 text-sm font-medium bg-muted/60 focus:bg-muted"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                            Rename
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={(e) => e.stopPropagation()}
                            className="rounded-lg px-2.5 py-2 gap-2 text-sm font-medium bg-muted/60 focus:bg-muted"
                          >
                            <FolderInput className="h-3.5 w-3.5" />
                            Move to
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={(e) => e.stopPropagation()}
                            className="rounded-lg px-2.5 py-2 gap-2 text-sm font-medium bg-muted/60 focus:bg-muted"
                          >
                            <Lock className="h-3.5 w-3.5" />
                            Make Private
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={(e) => e.stopPropagation()}
                            className="rounded-lg px-2.5 py-2 gap-2 text-sm font-medium bg-muted/60 focus:bg-muted"
                          >
                            <Merge className="h-3.5 w-3.5" />
                            Merge Folders
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={(e) => e.stopPropagation()}
                            className="rounded-lg px-2.5 py-2 gap-2 text-sm font-medium bg-muted/60 focus:bg-muted"
                          >
                            <Download className="h-3.5 w-3.5" />
                            Export Folder
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={(e) => e.stopPropagation()}
                            className="rounded-lg px-2.5 py-2 gap-2 text-sm font-medium bg-muted/60 focus:bg-muted"
                          >
                            <Info className="h-3.5 w-3.5" />
                            Info
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={(e) => e.stopPropagation()}
                            className="rounded-lg px-2.5 py-2 gap-2 text-sm font-medium bg-muted/60 focus:bg-muted"
                          >
                            <Eraser className="h-3.5 w-3.5" />
                            Empty Folder
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              handleArchiveFolder(folder);
                            }}
                            className="rounded-lg px-2.5 py-2 gap-2 text-sm font-medium bg-muted/60 focus:bg-muted"
                          >
                            <Archive className="h-3.5 w-3.5" />
                            Archive
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteFolder(folder);
                            }}
                            className="rounded-lg px-2.5 py-2 gap-2 text-sm font-medium text-red-500 focus:text-red-600"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  ))
                )}
              </div>
            </ScrollArea>

            {/* Create folder button */}
            <button
              className="w-full text-left px-4 py-2.5 rounded-lg text-primary hover:bg-muted active:scale-[0.98] transition text-sm font-medium border"
              onClick={() => {
                setOpenFolderPickerSheet(false);
                setFolderSearchQuery("");
                setIsCreatingFolder(true);
              }}
            >
              + Create new folder
            </button>
          </div>
        </SheetContent>
      </Sheet>
      <Dialog
        open={isCreatingFolder}
        onOpenChange={(open) => {
          if (!open) {
            setIsCreatingFolder(false);
            setNewFolderName("");
          }
        }}
      >
        <DialogContent className="sm:rounded-3xl rounded-3xl max-w-[360px] p-8 [&>button]:hidden">
          <DialogHeader className="items-center text-center space-y-4">
            <div className="h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center">
              <FolderIcon className="h-6 w-6 text-primary" />
            </div>
            <DialogTitle className="text-lg font-semibold">Create folder</DialogTitle>
          </DialogHeader>
          <Input
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleCreateFolder();
            }}
            placeholder="Folder name"
            autoFocus
            className="rounded-xl h-11"
          />
          <DialogFooter className="flex-row gap-3 sm:justify-center">
            <Button
              variant="outline"
              className="flex-1 rounded-xl h-11"
              onClick={() => {
                setIsCreatingFolder(false);
                setNewFolderName("");
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateFolder}
              className="flex-1 rounded-xl h-11 bg-gradient-to-r from-blue-500 to-violet-500 hover:from-blue-600 hover:to-violet-600 text-white border-0"
            >
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog
        open={renamingFolder !== null}
        onOpenChange={(open) => !open && setRenamingFolder(null)}
      >
        <DialogContent className="sm:rounded-3xl rounded-3xl max-w-[360px] p-8 [&>button]:hidden">
          <DialogHeader className="items-center text-center space-y-4">
            <div className="h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center">
              <FolderIcon className="h-6 w-6 text-primary" />
            </div>
            <DialogTitle className="text-lg font-semibold">Rename folder</DialogTitle>
          </DialogHeader>
          <Input
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") submitRenameFolder();
            }}
            placeholder="Folder name"
            autoFocus
            className="rounded-xl h-11"
          />
          <DialogFooter className="flex-row gap-3 sm:justify-center">
            <Button
              variant="outline"
              className="flex-1 rounded-xl h-11"
              onClick={() => setRenamingFolder(null)}
            >
              Cancel
            </Button>
            <Button
              onClick={submitRenameFolder}
              disabled={renameFolder.isPending}
              className="flex-1 rounded-xl h-11 bg-gradient-to-r from-blue-500 to-violet-500 hover:from-blue-600 hover:to-violet-600 text-white border-0"
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={confirmDeleteId !== null}
        onOpenChange={(open) => !open && setConfirmDeleteId(null)}
      >
        <DialogContent className="max-w-xs rounded-2xl [&>button]:hidden">
          <DialogHeader>
            <DialogTitle className="text-base">Delete note?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            This note will be moved to the Trash and then deleted forever from
            all your devices after 30 days.
          </p>
          <DialogFooter className="flex-row justify-end gap-4 sm:justify-end">
            <Button
              variant="ghost"
              className="w-auto px-3"
              onClick={() => setConfirmDeleteId(null)}
            >
              Cancel
            </Button>
            <Button
              variant="ghost"
              className="w-auto px-3 text-red-500 hover:text-red-600"
              onClick={() => {
                if (confirmDeleteId !== null) handleDelete(confirmDeleteId);
                setConfirmDeleteId(null);
              }}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog
        open={folderToDelete !== null}
        onOpenChange={(open) => !open && setFolderToDelete(null)}
      >
        <DialogContent className="max-w-xs rounded-2xl [&>button]:hidden">
          <DialogHeader>
            <DialogTitle className="text-base">
              Delete "{folderToDelete?.name}" ?
            </DialogTitle>
          </DialogHeader>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox
              checked={deleteNotesInside}
              onCheckedChange={(checked) => setDeleteNotesInside(!!checked)}
            />
            Delete all notes inside
          </label>
          <DialogFooter className="flex-row justify-end gap-4 sm:justify-end">
            <Button
              variant="ghost"
              className="w-auto px-3"
              onClick={() => setFolderToDelete(null)}
            >
              Cancel
            </Button>
            <Button
              variant="ghost"
              className="w-auto px-3 text-red-500 hover:text-red-600"
              onClick={submitDeleteFolder}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {activeFolderId != null && (
        <button
          type="button"
          onClick={() => {
            setSelectedNoteIdsToAdd(new Set());
            setOpenAddNotesSheet(true);
          }}
          className="fixed bottom-6 right-6 z-40 flex items-center gap-2 h-12 px-5 rounded-full gradient-primary text-white text-sm font-medium shadow-lg active:scale-95 transition-transform"
        >
          <Plus className="h-4 w-4" />
          Add notes
        </button>
      )}
      <Sheet open={openAddNotesSheet} onOpenChange={setOpenAddNotesSheet}>
        <SheetContent
          side="bottom"
          className="rounded-t-3xl max-h-[80vh] flex flex-col"
        >
          <div className="text-base font-semibold px-1 pb-2 shrink-0">
            Add notes to{" "}
            {folders.find((f) => f.id === activeFolderId)?.name ?? "folder"}
          </div>
          <ScrollArea className="flex-1 -mx-1 px-1">
            <div className="flex flex-col gap-1 pb-2">
              {notesNotInFolder.length === 0 ? (
                <p className="text-sm text-muted-foreground px-2 py-6 text-center">
                  No other notes to add.
                </p>
              ) : (
                notesNotInFolder.map((note) => (
                  <label
                    key={note.id}
                    className="flex items-center gap-3 px-2 py-2.5 rounded-lg hover:bg-muted cursor-pointer"
                  >
                    <Checkbox
                      checked={selectedNoteIdsToAdd.has(note.id)}
                      onCheckedChange={() => toggleSelectedNoteToAdd(note.id)}
                    />
                    <span className="text-sm truncate">
                      {note.title || stripHtml(note.content) || "Untitled"}
                    </span>
                  </label>
                ))
              )}
            </div>
          </ScrollArea>
          <Button
            className="w-full shrink-0"
            disabled={selectedNoteIdsToAdd.size === 0}
            onClick={handleAddSelectedNotes}
          >
            Add {selectedNoteIdsToAdd.size > 0 ? selectedNoteIdsToAdd.size : ""}{" "}
            note
            {selectedNoteIdsToAdd.size === 1 ? "" : "s"}
          </Button>
        </SheetContent>
      </Sheet>
      {openNote && <NoteOverlay note={openNote} onClose={handleCloseOverlay} />}
    </div>
  );
}
